/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import heroImage from '/images/404-hero.jpg';

const BURGUNDY = '#6B1F2E';

export default function NotFound404({ homepageUrl }: { homepageUrl: string }): ReactElement {
    return (
        <main className="grow">
            <section className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center bg-background px-6 py-16 lg:py-24">
                <div className="mx-auto flex w-full max-w-5xl flex-col items-center text-center">
                    <img src={heroImage} alt="404" className="w-full max-w-[720px] h-auto select-none" />

                    <h1 className="sr-only">404 — Page not found</h1>

                    <p
                        className="mt-8 max-w-2xl font-display text-xl sm:text-2xl lg:text-3xl font-medium italic leading-snug"
                        style={{ color: BURGUNDY }}>
                        We checked every pocket—this page isn&rsquo;t there.
                    </p>

                    <div className="mt-10">
                        <a
                            href={homepageUrl}
                            className="inline-block bg-[#1a1a1a] px-10 py-4 text-xs font-medium uppercase tracking-[0.12em] text-primary-foreground no-underline transition-colors duration-200 hover:bg-[#333]">
                            Back to Homepage
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}
